import { useEffect, useCallback, useMemo, useState, useRef } from "react";
import { useWindowDimensions, Platform, StyleSheet, TouchableOpacity } from "react-native";
import { View, Text } from "@src/components";
import { useRouter } from "expo-router";
import * as ScreenOrientation from "expo-screen-orientation";
import { useUnistyles } from "react-native-unistyles";
import { Image } from "expo-image";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useLivestream } from "@src/features/cameras";
import { DataStateView } from "@src/components/screen-state";
import { useCameraRouteParams } from "@src/hooks/useCameraRouteParams";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import { useTenantId } from "@src/features/tenants";
import { LivestreamMjpegView } from "@src/components/LivestreamMjpegView";
import { StreamingShell } from "@src/features/cameras/components";
import { useLandscapeLock } from "@src/features/cameras/hooks";
import { useStillWatching } from "@src/features/cameras/hooks/useStillWatching";
import { cameraHoldsFeature } from "@src/features/cameras/utils/camera-features";
import { Icon } from "@src/components/Icon";
import { useIsOnline } from "@src/utils/network";
import { LivestreamErrorBoundary } from "./LivestreamErrorBoundary";
import { StreamTimeoutView } from "./StreamTimeoutView";
import { StreamErrorView } from "./StreamErrorView";
import { StillWatchingView } from "./StillWatchingView";
import { OfflineSnapshotOverlay } from "./OfflineSnapshotOverlay";
import { BufferingOverlay } from "./BufferingOverlay";
import { isWeb } from "@src/utils/web-helpers";
import { useCameraPermissions } from "@visioni/a3s-access/app";
import { useRecordActivity } from "@visioni/a3s-activity/app";
import { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import { useOpenGate } from "@visioni/a3s-gate-control/app";
import { universalConfirm } from "@src/components/UniversalAlert";
import { showErrorToast } from "@src/utils/toast";

const generateMp4HTML = (url: string) => `
  <!DOCTYPE html>
  <html>
  <head>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      html, body { width: 100%; height: 100%; background: black; }
      video { width: 100%; height: 100%; object-fit: contain; }
    </style>
  </head>
  <body>
    <video autoplay controls muted playsinline>
      <source src="${url}" type="video/mp4">
    </video>
    <script>
      var reported = false;
      function reportError() {
        if (reported) return;
        reported = true;
        window.parent.postMessage({ type: 'livestream-error' }, '*');
      }
      document.querySelector('source').addEventListener('error', reportError);
      document.querySelector('video').addEventListener('error', reportError);
    </script>
  </body>
  </html>
`;

const generateMjpegHTML = (url: string) => `
  <!DOCTYPE html>
  <html>
  <head>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      html, body { width: 100%; height: 100%; background: black; overflow: hidden; }
      img { width: 100%; height: 100%; object-fit: contain; }
    </style>
  </head>
  <body>
    <img src="${url}" alt="" />
    <script>
      document.querySelector('img').addEventListener('error', function() {
        window.parent.postMessage({ type: 'livestream-error' }, '*');
      });
    </script>
  </body>
  </html>
`;

const isMjpegFormat = (format?: string | null) => format === "mjpeg" || format === "http-mjpeg" || format === "mjpg";

const STREAM_TIMEOUT_MS = 15_000;

export function LivestreamContent() {
  const { siteId, cameraKey } = useCameraRouteParams({
    requireSiteId: false,
    requireCameraKey: true,
  });

  const { theme } = useUnistyles();
  const router = useRouter();
  const isOnline = useIsOnline();
  const { track } = useAmplitude();
  const tenantId = useTenantId();
  const mediaViewPathParams = useMemo(() => {
    if (!siteId || !cameraKey) return undefined;
    return {
      siteId: SiteId.fromString(siteId),
      assetCameraKey: AssetCameraKey.fromString(cameraKey),
    };
  }, [siteId, cameraKey]);
  const { mutate: recordActivity } = useRecordActivity(mediaViewPathParams);
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const framePadding = theme.spacing["5xl"];
  const targetAspectRatio = 16 / 9;
  const usableWidth = Math.max(0, screenWidth - framePadding * 2);
  const screenAspectRatio = usableWidth / screenHeight;
  const playerWidth = screenAspectRatio > targetAspectRatio ? screenHeight * targetAspectRatio : usableWidth;
  const playerHeight = screenAspectRatio > targetAspectRatio ? screenHeight : usableWidth / targetAspectRatio;
  const logLayoutOnceRef = useRef(false);
  const mountTimeRef = useRef(Date.now());
  const streamUrlReadyRef = useRef<number | null>(null);
  const cameraDataReadyRef = useRef<number | null>(null);
  const hasTrackedStart = useRef(false);
  const sessionGenRef = useRef(0);

  const { camera, isCameraLoading, cameraError, renewSession, hasLivestream, streamUrl, streamFormat } = useLivestream({
    siteId,
    cameraKey,
  });

  const { canViewLivestream, isLoading: permissionsLoading } = useCameraPermissions(siteId, cameraKey);

  const insets = useSafeAreaInsets();
  const showGateButton = cameraHoldsFeature(camera, "gate");
  const openGateMutation = useOpenGate();

  const handleOpenGate = useCallback(async () => {
    const confirmed = await universalConfirm({
      title: "Open Gate",
      message: "Are you sure you want to open the gate?",
      confirmLabel: "Open",
    });
    if (!confirmed) return;

    openGateMutation.mutate(
      { siteId: siteId ?? "", cameraKey },
      {
        onError: (err: unknown) => {
          showErrorToast({ error: err, options: { containerStyle: { maxWidth: 400, alignSelf: "center" } } });
        },
      },
    );
  }, [siteId, cameraKey, openGateMutation]);

  const [isBuffering, setIsBuffering] = useState(true);
  const [snapshotError, setSnapshotError] = useState(false);
  const [showCloseButton, setShowCloseButton] = useState(true);
  const [streamTimedOut, setStreamTimedOut] = useState(false);
  const [streamError, setStreamError] = useState(false);

  const prefersMjpeg = useMemo(() => isMjpegFormat(streamFormat), [streamFormat]);
  const snapshotUrl = camera?.snapshot?.url ?? null;
  const snapshotCapturedAt = camera?.snapshot?.capturedAt ?? null;
  const showSnapshotOverlay = !!snapshotUrl && !snapshotError && (isBuffering || !streamUrl);
  const snapshotCacheKey =
    snapshotCapturedAt && cameraKey ? `${cameraKey}:${snapshotCapturedAt}`
    : cameraKey ? `${cameraKey}:${Math.floor(Date.now() / (5 * 60 * 1000))}`
    : `${Math.floor(Date.now() / (5 * 60 * 1000))}`;

  const { reset: resetStream, ...stillWatching } = useStillWatching({
    isActive: !isBuffering && !!streamUrl && !streamTimedOut && !streamError,
    siteId,
    cameraKey,
    onRefresh: () => {
      sessionGenRef.current++;
      setStreamTimedOut(false);
      setStreamError(false);
      setIsBuffering(true);
      hasTrackedStart.current = false;
      mountTimeRef.current = Date.now();
      cameraDataReadyRef.current = null;
      streamUrlReadyRef.current = null;
      void renewSession();
    },
    onClose: () => void handleClose(),
  });

  // Track livestream viewed on mount
  useEffect(() => {
    track(ANALYTICS_EVENTS.LIVESTREAM_VIEWED, {
      camera_key: cameraKey,
      tenant_id: tenantId,
      site_id: siteId,
    });
  }, [track, cameraKey, tenantId, siteId]);

  // Timing: log when camera data and stream URL become available
  useEffect(() => {
    if (camera) {
      cameraDataReadyRef.current = Date.now();
      console.log(`[Livestream] Camera data ready (+${Date.now() - mountTimeRef.current}ms)`);
    }
  }, [camera]);

  useEffect(() => {
    if (streamUrl) {
      streamUrlReadyRef.current = Date.now();
      console.log(`[Livestream] Stream URL ready (+${Date.now() - mountTimeRef.current}ms)`);
    }
  }, [streamUrl]);

  // Stream connection timeout — if buffering persists for 15s after streamUrl is set
  useEffect(() => {
    if (!streamUrl || !isBuffering || streamTimedOut) return;

    const timer = setTimeout(() => {
      setStreamTimedOut(true);
      track(ANALYTICS_EVENTS.LIVESTREAM_TIMEOUT, {
        camera_key: cameraKey,
        tenant_id: tenantId,
        site_id: siteId,
        duration_ms: Date.now() - mountTimeRef.current,
      });
    }, STREAM_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [streamUrl, isBuffering, streamTimedOut, track, cameraKey, tenantId, siteId]);

  // Listen for stream errors from the iframe (web only)
  useEffect(() => {
    if (Platform.OS !== "web") return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "livestream-error") {
        setStreamError(true);
        setIsBuffering(false);
        resetStream();
        track(ANALYTICS_EVENTS.LIVESTREAM_ERROR, {
          camera_key: cameraKey,
          tenant_id: tenantId,
          site_id: siteId,
          duration_ms: Date.now() - mountTimeRef.current,
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [track, cameraKey, tenantId, siteId, resetStream]);

  // Track stream started with timing when buffering completes
  useEffect(() => {
    if (!isBuffering && streamUrl && !hasTrackedStart.current) {
      hasTrackedStart.current = true;
      const now = Date.now();
      const totalDurationMs = now - mountTimeRef.current;
      const cameraDataMs = cameraDataReadyRef.current ? cameraDataReadyRef.current - mountTimeRef.current : null;
      const streamUrlMs = streamUrlReadyRef.current ? streamUrlReadyRef.current - mountTimeRef.current : null;
      const streamConnectMs = streamUrlReadyRef.current ? now - streamUrlReadyRef.current : null;

      track(ANALYTICS_EVENTS.LIVESTREAM_STARTED, {
        camera_key: cameraKey,
        tenant_id: tenantId,
        site_id: siteId,
        duration_ms: totalDurationMs,
        camera_data_ready_ms: cameraDataMs,
        stream_url_ready_ms: streamUrlMs,
        stream_connect_ms: streamConnectMs,
      });
      if (mediaViewPathParams) {
        recordActivity(
          { kind: "livestream_viewed" },
          {
            onError: (error) => {
              console.warn("Failed to record livestream view activity:", error);
            },
          },
        );
      }
      console.log(`[Livestream] Stream started (+${totalDurationMs}ms)`);
    }
  }, [isBuffering, streamUrl, track, cameraKey, tenantId, siteId, mediaViewPathParams, recordActivity]);

  // Hide close button after buffering completes
  useEffect(() => {
    if (!isBuffering && !isWeb) {
      setShowCloseButton(false);
    }
  }, [isBuffering]);

  // Reset snapshot error when camera or snapshot changes
  useEffect(() => {
    setSnapshotError(false);
  }, [cameraKey, snapshotUrl, snapshotCapturedAt]);

  // Handle orientation lock
  useLandscapeLock({ enabled: hasLivestream, delay: 500 });

  // Set buffering when stream URL changes
  useEffect(() => {
    if (streamUrl) {
      setIsBuffering(true);
      setStreamTimedOut(false);
      setStreamError(false);
      resetStream();
    }
  }, [streamUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = useCallback(async () => {
    try {
      await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
    } catch (error) {
      // Orientation lock not supported, continue with navigation
    }

    setTimeout(() => {
      router.back();
    }, 250);
  }, [router]);

  const handleRetry = useCallback(() => {
    sessionGenRef.current++;
    setStreamTimedOut(false);
    setStreamError(false);
    setIsBuffering(true);
    hasTrackedStart.current = false;
    mountTimeRef.current = Date.now();
    cameraDataReadyRef.current = null;
    streamUrlReadyRef.current = null;
    resetStream();
    void renewSession();
  }, [renewSession, resetStream]);

  const handleSnapshotError = useCallback(() => {
    setSnapshotError(true);
  }, []);

  const renderVideoPlayer = () => {
    if (!streamUrl) {
      if (showSnapshotOverlay) {
        return <View style={{ flex: 1 }} />;
      }
      return <BufferingOverlay />;
    }

    if (streamTimedOut) {
      return <StreamTimeoutView onRetry={handleRetry} />;
    }

    if (streamError) {
      return <StreamErrorView onRetry={handleRetry} />;
    }

    if (Platform.OS === "web") {
      return (
        <iframe
          key={sessionGenRef.current}
          srcDoc={prefersMjpeg ? generateMjpegHTML(streamUrl) : generateMp4HTML(streamUrl)}
          style={{ width: playerWidth, height: playerHeight, border: "none" }}
          allow="autoplay; fullscreen"
          onLoad={() => setIsBuffering(false)}
        />
      );
    }

    if (Platform.OS === "ios" || Platform.OS === "android") {
      return (
        <LivestreamMjpegView
          source={streamUrl}
          style={StyleSheet.absoluteFill}
          onLoadStart={() => {
            setIsBuffering(true);
          }}
          onLoadEnd={() => {
            setIsBuffering(false);
          }}
          onSingleTap={() => {
            setShowCloseButton((prev) => !prev);
          }}
          onError={() => {
            setIsBuffering(false);
            track(ANALYTICS_EVENTS.LIVESTREAM_ERROR, {
              camera_key: cameraKey,
              tenant_id: tenantId,
              site_id: siteId,
              duration_ms: Date.now() - mountTimeRef.current,
            });
          }}
        />
      );
    }

    return (
      <View
        style={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          padding: theme.spacing["3xl"],
        }}
      >
        <Icon
          name="errorOutline"
          size={64}
          color={theme.colors.white.default}
          style={{ marginBottom: theme.spacing.xl }}
        />
        <Text textStyle="lg/bold" style={{ color: theme.colors.white.default, textAlign: "center" }} mb="lg">
          Platform Not Supported
        </Text>
        <Text textStyle="md/regular" style={{ color: theme.colors.grey[400], textAlign: "center" }}>
          Live stream is supported on iOS and Web. MJPEG streams are supported on Android.
        </Text>
      </View>
    );
  };

  const renderContent = () => {
    // If offline, show snapshot with offline message (don't attempt native connection)
    if (!isOnline && snapshotUrl && !snapshotError) {
      return (
        <OfflineSnapshotOverlay
          snapshotUrl={snapshotUrl}
          snapshotCacheKey={snapshotCacheKey}
          playerWidth={playerWidth}
          playerHeight={playerHeight}
          onSnapshotError={handleSnapshotError}
        />
      );
    }

    return (
      <View flex={1} bg="black">
        <View
          style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
          onLayout={(event) => {
            if (!logLayoutOnceRef.current) {
              const { width, height } = event.nativeEvent.layout;
              console.log("[LivestreamLayout] container", { width, height, screenWidth, screenHeight });
            }
          }}
        >
          <View
            style={{ width: playerWidth, height: playerHeight, position: "relative" }}
            onLayout={(event) => {
              if (!logLayoutOnceRef.current) {
                const { width, height } = event.nativeEvent.layout;
                console.log("[LivestreamLayout] playerBox", { width, height });
              }
            }}
          >
            {renderVideoPlayer()}
            {stillWatching.showPrompt && (
              <StillWatchingView
                autoStopSeconds={stillWatching.autoStopSeconds}
                onContinue={stillWatching.handleContinueWatching}
                onStop={stillWatching.handleStopWatching}
              />
            )}
            {showSnapshotOverlay && !streamTimedOut && (
              <View
                style={StyleSheet.absoluteFillObject}
                onLayout={(event) => {
                  if (!logLayoutOnceRef.current) {
                    const { width, height } = event.nativeEvent.layout;
                    console.log("[LivestreamLayout] overlay", { width, height });
                    logLayoutOnceRef.current = true;
                  }
                }}
              >
                <Image
                  source={{ uri: snapshotUrl, cacheKey: snapshotCacheKey } as any}
                  style={StyleSheet.absoluteFillObject}
                  contentFit="cover"
                  cachePolicy="memory-disk"
                  onError={handleSnapshotError}
                />
                <BufferingOverlay />
              </View>
            )}
          </View>
        </View>
        {!showSnapshotOverlay && isBuffering && streamUrl && !streamTimedOut ?
          <BufferingOverlay />
        : null}
      </View>
    );
  };

  const isEmptyCheck = useCallback((cam: any) => {
    return !cam?.livestreams?.length;
  }, []);

  if (permissionsLoading) {
    return (
      <LivestreamErrorBoundary>
        <StreamingShell onClose={handleClose} showCloseButton>
          <View flex={1} bg="black" style={{ justifyContent: "center", alignItems: "center" }}>
            <BufferingOverlay />
          </View>
        </StreamingShell>
      </LivestreamErrorBoundary>
    );
  }

  if (!canViewLivestream) {
    return (
      <LivestreamErrorBoundary>
        <StreamingShell onClose={handleClose} showCloseButton>
          <View flex={1} bg="black" style={{ justifyContent: "center", alignItems: "center" }}>
            {snapshotUrl && !snapshotError ?
              <View style={{ width: playerWidth, height: playerHeight, position: "relative" }}>
                <Image
                  source={{ uri: snapshotUrl, cacheKey: snapshotCacheKey } as any}
                  style={StyleSheet.absoluteFillObject}
                  contentFit="cover"
                  cachePolicy="memory-disk"
                  onError={handleSnapshotError}
                />
                <View
                  style={{
                    ...StyleSheet.absoluteFillObject,
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "rgba(0,0,0,0.5)",
                  }}
                >
                  <Icon
                    name="videoOff"
                    size={48}
                    color={theme.colors.white.default}
                    style={{ marginBottom: theme.spacing.lg }}
                  />
                  <Text textStyle="lg/bold" style={{ color: theme.colors.white.default, textAlign: "center" }} mb="sm">
                    Live View Restricted
                  </Text>
                  <Text textStyle="md/regular" style={{ color: theme.colors.grey[400], textAlign: "center" }}>
                    You don&apos;t have permission to view the livestream.
                  </Text>
                </View>
              </View>
            : <View style={{ padding: theme.spacing["3xl"], alignItems: "center" }}>
                <Icon
                  name="videoOff"
                  size={64}
                  color={theme.colors.white.default}
                  style={{ marginBottom: theme.spacing.xl }}
                />
                <Text textStyle="lg/bold" style={{ color: theme.colors.white.default, textAlign: "center" }} mb="sm">
                  Live View Restricted
                </Text>
                <Text textStyle="md/regular" style={{ color: theme.colors.grey[400], textAlign: "center" }}>
                  You don&apos;t have permission to view the livestream.
                </Text>
              </View>
            }
          </View>
        </StreamingShell>
      </LivestreamErrorBoundary>
    );
  }

  return (
    <LivestreamErrorBoundary>
      <StreamingShell onClose={handleClose} showCloseButton={showCloseButton}>
        <DataStateView
          data={camera}
          isLoading={isCameraLoading}
          isFetching={isCameraLoading}
          error={cameraError}
          refetch={handleRetry}
          isEmpty={isEmptyCheck}
          showStaleDataBanner={false}
          loadingMessage="Loading camera..."
          errorTitle="Unable to stream"
          errorMessage="Failed to start live stream. Please try again."
          emptyState={{
            icon: "videoOff",
            title: "Live stream not available",
            message: "This camera does not support live streaming.",
          }}
        >
          {() => renderContent()}
        </DataStateView>

        {showGateButton && (
          <TouchableOpacity
            onPress={handleOpenGate}
            disabled={openGateMutation.isPending}
            activeOpacity={0.7}
            style={{
              position: "absolute",
              bottom: insets.bottom + 16,
              right: insets.right + 16,
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              height: 44,
              paddingHorizontal: 16,
              borderRadius: 22,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
            }}
          >
            <Icon name="lockOpen" size={18} color={theme.colors.white.default} />
            <Text textStyle="sm/semibold" color="white">
              Open Gate
            </Text>
          </TouchableOpacity>
        )}
      </StreamingShell>
    </LivestreamErrorBoundary>
  );
}
